/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.onboarding.profile

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.onboarding.generated.resources.Res
import studio.oddpods.cauce.feature.onboarding.generated.resources.onboarding_profile_error_save_failed

data class ProfileSetupState(
    val displayName: String = "",
    val feeInput: String = "",
    val error: UiText? = null,
    val isSaving: Boolean = false,
)

sealed class ProfileSetupAction {
    data class DisplayNameChanged(val name: String) : ProfileSetupAction()
    data class FeeChanged(val fee: String) : ProfileSetupAction()
    data object SaveClick : ProfileSetupAction()
}

sealed class ProfileSetupEvent {
    data object NavigateNext : ProfileSetupEvent()
}

class ProfileSetupViewModel(
    private val profileRepository: ProfileRepository,
    private val currencyFormatter: CurrencyFormatter,
) : BaseViewModel<ProfileSetupState, ProfileSetupEvent, ProfileSetupAction>(ProfileSetupState()) {

    override fun handleAction(action: ProfileSetupAction) {
        when (action) {
            is ProfileSetupAction.DisplayNameChanged -> {
                mutableStateFlow.value = state.copy(displayName = action.name, error = null)
            }
            is ProfileSetupAction.FeeChanged -> {
                mutableStateFlow.value = state.copy(feeInput = action.fee, error = null)
            }
            ProfileSetupAction.SaveClick -> save()
        }
    }

    private fun save() {
        val name = state.displayName.trim()
        if (name.isBlank()) {
            mutableStateFlow.value = state.copy(error = UiText.Dynamic("Ingresá tu nombre"))
            return
        }
        val centavos = currencyFormatter.parse(state.feeInput)
        if (centavos == null || centavos < 10_000L) {
            mutableStateFlow.value = state.copy(error = UiText.Dynamic("El honorario debe ser al menos \$100"))
            return
        }
        mutableStateFlow.value = state.copy(isSaving = true, error = null)
        viewModelScope.launch {
            profileRepository.updateDisplayNameAndFee(name, centavos)
                .onSuccess {
                    mutableStateFlow.value = state.copy(isSaving = false)
                    sendEvent(ProfileSetupEvent.NavigateNext)
                }
                .onFailure { t ->
                    mutableStateFlow.value = state.copy(
                        isSaving = false,
                        error = t.toUiText(Res.string.onboarding_profile_error_save_failed),
                    )
                }
        }
    }
}
